import {
  createContext,
  Fragment,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import { useQueryState } from 'nuqs';

import type { Optional } from 'types';
import { startCriticalFlowRecording, stopCriticalFlowRecording } from 'utils/sessionRecording';

import { EXCLUDED_MODALS_FROM_PERSISTENT } from './constants';
import type { ModalContextValue, Modal, ModalRenderFn, ShowModalFn } from './types';
import { useModalState } from './useModalState';

type ModalProvider = {
  children: ReactNode;
};

function isExcluded(id?: string) {
  return !!id && EXCLUDED_MODALS_FROM_PERSISTENT.includes(id);
}

export function createModalManager<ModalMap extends Modal<string, Record<string, unknown>>>(
  renderFn: ModalRenderFn<ModalMap>,
  // Modal ids that are part of a critical flow (e.g. extending a proxy) - session recording is
  // turned on for the whole time any of these is open, regardless of whether an error occurs.
  criticalRecordingModalIds: ReadonlySet<string> = new Set(),
) {
  const ModalContext = createContext<Optional<ModalContextValue<ModalMap>>>(undefined);

  function Provider({ children }: ModalProvider) {
    const didInitRef = useRef(false);

    const modals = useModalState((state) => state.modals);
    const modalsRef = useRef(modals);
    useLayoutEffect(() => {
      modalsRef.current = modals;
    });
    const hideModalFromState = useModalState((state) => state.hideModal);
    const showModalFromState = useModalState((state) => state.showModal);

    const [modalId, setModalId] = useQueryState('modal', { defaultValue: '' });
    const [, setStringifiedModalParams] = useQueryState('modalParams');

    const showModal = useCallback<ShowModalFn<ModalMap>>(
      (id, ...args) => {
        const params = args[0] ?? undefined;

        showModalFromState(id, params);
      },
      [showModalFromState],
    );

    const hideModal = useCallback(
      <ID extends ModalMap['id']>(id?: ID) => {
        setModalId(null);
        setStringifiedModalParams(null);

        hideModalFromState(id);
      },
      [hideModalFromState, setModalId, setStringifiedModalParams],
    );

    const renderModals = useCallback(() => {
      return modals.map((modal, index) => (
        <Fragment key={`modal-${index}-${modal.id}`}>{renderFn(modal as ModalMap)}</Fragment>
      ));
    }, [modals]);

    const value = useMemo<ModalContextValue<ModalMap>>(() => {
      return { modals, showModal, hideModal };
    }, [hideModal, modals, showModal]);

    // Restores modal from URL on navigation. Reads modals via ref so Zustand
    // updates don't re-trigger this — avoids re-opening a modal that was just
    // closed while the nuqs URL update is still pending.
    //
    // Params are never written to the URL — they only ever come from the persisted
    // (sessionStorage) store. If a modal id is present in the URL but the store has no
    // matching entry, there's no way to reconstruct its params, so drop the stale
    // reference instead of opening the modal with `params: undefined`.
    useEffect(() => {
      if (!modalId.length) return;
      if (isExcluded(modalId)) return;
      if (modalsRef.current.some((modal) => modal.id === modalId)) return;

      setModalId(null);
    }, [modalId, setModalId]);

    useEffect(() => {
      const topModal = modals[modals.length - 1];

      if (topModal) {
        setModalId(topModal.id);
      } else {
        setModalId(null);
        setStringifiedModalParams(null);
      }
    }, [modals, setModalId, setStringifiedModalParams]);

    useEffect(() => {
      if (didInitRef.current) return;
      didInitRef.current = true;

      if (isExcluded(modalId)) {
        setModalId(null);
        setStringifiedModalParams(null);
      }
    }, [modalId, setModalId, setStringifiedModalParams]);

    useEffect(() => {
      if (!criticalRecordingModalIds.size) return;

      const hasCriticalModalOpen = modals.some((modal) => criticalRecordingModalIds.has(modal.id));

      if (hasCriticalModalOpen) {
        startCriticalFlowRecording('critical-modal');
      } else {
        stopCriticalFlowRecording('critical-modal');
      }
    }, [modals]);

    return (
      <ModalContext.Provider value={value}>
        {children}

        <Suspense fallback={null}>{renderModals()}</Suspense>
      </ModalContext.Provider>
    );
  }

  function useModalManager() {
    const context = useContext(ModalContext);

    if (context === undefined) {
      throw new Error('useModalManager must be used within ModalProvider');
    }

    return context;
  }

  function useShowModal() {
    const { showModal } = useModalManager();

    return showModal;
  }

  function useHideModal() {
    const { hideModal } = useModalManager();

    return hideModal;
  }

  function useModalStack() {
    const { modals } = useModalManager();

    return modals;
  }

  return { Provider, useShowModal, useHideModal, useModalStack, useModalManager };
}
